/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, forwardRef, Input, OnChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';

import {
  buildCountryOptions,
  Country,
  DEFAULT_COUNTRY_PRIO
} from './countries';

@Component({
  standalone: true,
  selector: 'adn-country-select',
  imports: [CommonModule, FormsModule],
  template: `
    <select
      class="form-control"
      [style.width]="autoWidth ? 'auto' : null"
      [disabled]="disabled"
      [ngModel]="value"
      [ngModelOptions]="{standalone: true}"
      (ngModelChange)="onValueChange($event)"
      (blur)="onTouched()">
      <option *ngIf="allowEmpty" value=""></option>
      <option *ngFor="let country of countries" [value]="country.code" [disabled]="country.disabled">{{ formatCountry(country) }}</option>
    </select>
  `,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AdnCountrySelectComponent),
    multi: true
  }]
})
export class AdnCountrySelectComponent implements ControlValueAccessor, OnChanges {
  @Input() countryPrio: Array<string> = DEFAULT_COUNTRY_PRIO;
  @Input() allowEmpty = true;
  @Input() autoWidth = false;
  @Input() labelMode: 'name' | 'code' | 'codeAndName' = 'name';
  @Input() showPrioritySeparator = true;

  countries: Array<Country> = [];
  disabled = false;
  value = '';

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnChanges(): void {
    this.countries = buildCountryOptions(this.countryPrio, this.showPrioritySeparator);
  }

  writeValue(value: string | null | undefined): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string): void {
    this.value = value;
    this.onChange(value);
  }

  formatCountry(country: Country): string {
    if (this.labelMode === 'code') {
      return country.code;
    }
    if (this.labelMode === 'codeAndName') {
      return country.code + ' - ' + country.name;
    }
    return country.name;
  }
}
